import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Bike, Cog, Mail, MapPin, SlidersHorizontal, Wrench } from "lucide-react";
import { MaskedLines, Reveal, SectionHeading } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import { fetchProducts, formatNZD } from "@/lib/api";

const HERO_IMG =
  "https://customer-assets-jai6qajn.emergentagent.net/job_ebike-builder-7/artifacts/11556fb3c5360fa7_IMG_4355.jpeg";
const STORY_IMG =
  "https://images.pexels.com/photos/33968618/pexels-photo-33968618.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TRUST = [
  { icon: MapPin, label: "NZ Based", note: "Auckland owned & operated" },
  { icon: Cog, label: "Quality Components", note: "Hand-picked, proven parts" },
  { icon: Wrench, label: "Torque Arms Included", note: "$170 shipping & pickup — $200 installation" },
];

const STEPS = [
  {
    icon: SlidersHorizontal,
    title: "Configure your combo",
    body: "Pick your display and battery — the price updates as you go. Everything else is included.",
  },
  {
    icon: Mail,
    title: "We confirm & invoice",
    body: "Place your order and we confirm stock with our supplier, then email you a manual invoice. No card needed online.",
  },
  {
    icon: Bike,
    title: "Pay & ride",
    body: "Once paid we ship to your door or you pick up in Auckland — $170 flat. Want it fitted? Full installation is $200. Accessories ride along free.",
  },
];

const MARQUEE_ITEMS = [
  "Power Your Ride",
  "2000W Motors",
  "Auckland E-Kits",
  "Premium Conversions",
  "Torque Arms Included",
  "NZ Wide Shipping",
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 900], [0, 140]);

  useEffect(() => {
    fetchProducts()
      .then((data) => {
        setProducts(data.filter((p) => p.featured).slice(0, 2));
        setAccessories(data.filter((p) => p.badge === "Accessory"));
      })
      .catch(() => setProducts([]));
  }, []);

  return (
    <div>
      {/* ---------- HERO ---------- */}
      <section className="relative flex min-h-[92vh] items-center overflow-hidden bg-white">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-surface/60 via-white to-white" />

        <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-12 px-5 pt-24 pb-16 sm:px-8 lg:grid-cols-2">
          <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="eyebrow mb-6 flex items-center gap-3"
            data-testid="hero-eyebrow"
          >
            <span className="inline-block h-2 w-2 rounded-full bg-volt" />
            Auckland, New Zealand — E-Bike Conversion Kits
          </motion.p>

          <h1
            className="font-display font-black tracking-tight text-ink leading-[0.95]"
            style={{ fontSize: "clamp(2.8rem, 7.5vw, 6.5rem)" }}
            data-testid="hero-heading"
          >
            <MaskedLines
              delay={0.15}
              lines={[
                <span key="a">TURN YOUR</span>,
                <span key="b">
                  BIKE <span className="text-volt">ELECTRIC.</span>
                </span>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-zinc-700"
            data-testid="hero-subheadline"
          >
            Premium conversion kits, configured your way — motor, battery,
            controller and display. Shipped to your door or picked up in
            Auckland for $170 flat — or fully installed by us for $200.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/shop"
              data-testid="hero-cta-shop"
              className="group inline-flex items-center gap-2 rounded-[3px] bg-volt px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-volt-dim"
            >
              Shop Kits
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/accessories"
              data-testid="hero-cta-accessories"
              className="group inline-flex items-center gap-2 rounded-[3px] border border-black/25 bg-white/60 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-ink transition-colors duration-200 hover:border-volt hover:text-volt"
            >
              View Accessories
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-14 grid max-w-3xl gap-4 sm:grid-cols-3"
          >
            {TRUST.map((t) => (
              <div
                key={t.label}
                className="flex items-start gap-3 rounded-xl border border-black/10 bg-white/85 p-4 backdrop-blur-sm"
                data-testid={`trust-badge-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <t.icon className="mt-0.5 h-5 w-5 shrink-0 text-volt" strokeWidth={2.2} />
                <div>
                  <p className="font-display text-sm font-bold text-ink">{t.label}</p>
                  <p className="mt-0.5 text-xs text-zinc-500">{t.note}</p>
                </div>
              </div>
            ))}
          </motion.div>
          </div>

          <motion.div style={{ y: yImg }} className="relative">
            <div className="overflow-hidden rounded-2xl border border-black/10 bg-white shadow-[0_0_80px_-28px_rgba(0,87,255,0.5)]">
              <img
                src={HERO_IMG}
                alt="2000W conversion kit — motor wheel, controller, display, battery and charger"
                className="w-full object-cover"
                loading="eager"
              />
            </div>
            <span className="absolute bottom-4 left-4 rounded-full border border-white/20 bg-ink/90 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white backdrop-blur-sm">
              From {products[0] ? formatNZD(products[0].base_price) : "$1,500"} — everything included
            </span>
          </motion.div>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      {/* ---------- HOW IT WORKS ---------- */}
      <section className="mx-auto max-w-7xl px-5 pt-24 sm:px-8">
        <SectionHeading eyebrow="How it works" title="Ordering, minus the mystery." />
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1} className="h-full">
              <div className="relative h-full rounded-2xl border border-black/10 bg-white p-8 transition-colors duration-300 hover:border-volt/40" data-testid={`how-step-${i}`}>
                <span className="font-mono text-sm font-bold text-volt">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <s.icon className="mt-4 h-7 w-7 text-volt" strokeWidth={2} />
                <h3 className="mt-4 font-display text-lg font-extrabold tracking-tight text-ink">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- FEATURED COMBOS ---------- */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading eyebrow="The Lineup" title="Combos that convert." />
          <Reveal delay={0.1}>
            <Link
              to="/shop"
              data-testid="home-view-all-link"
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 transition-colors duration-200 hover:text-volt"
            >
              View all kits <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} delay={i * 0.1} eager={i === 0} />
          ))}
          {products.length === 0 && (
            <p className="text-zinc-500">Loading kits…</p>
          )}
        </div>
      </section>

      {/* ---------- ACCESSORIES ---------- */}
      {accessories.length > 0 && (
        <section className="border-y border-black/10 bg-surface">
          <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <SectionHeading eyebrow="Add-ons" title="The finishing touches." />
              <Reveal delay={0.1}>
                <Link
                  to="/accessories"
                  data-testid="home-accessories-link"
                  className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 transition-colors duration-200 hover:text-volt"
                >
                  All accessories <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {accessories.map((p, i) => (
                <ProductCard key={p.id} product={p} delay={i * 0.08} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- BRAND STORY TEASER ---------- */}
      <section className="border-b border-black/10">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 sm:px-8 lg:grid-cols-2 lg:items-center">
          <Reveal className="relative overflow-hidden rounded-2xl border border-black/10">
            <img
              src={STORY_IMG}
              alt="Cyclist on an Auckland street"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover"
            />
            <span className="absolute bottom-4 left-4 rounded-full border border-black/10 bg-white/85 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 backdrop-blur-sm">
              Tamaki Makaurau — Auckland
            </span>
          </Reveal>
          <div>
            <SectionHeading
              eyebrow="Brand Story"
              title={
                <>
                  From our garage
                  <br />
                  to your handlebars.
                </>
              }
            />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-zinc-600">
                Auckland E-Kits is a small, Auckland-based outfit with one
                obsession: making bikes fast without selling you a whole new
                bike. We hand-pick every motor, battery and controller, and
                every combo kit ships with torque arms as standard.
              </p>
              <p className="mt-4 text-base leading-relaxed text-zinc-600">
                You order online — we confirm stock with our supplier, invoice
                you directly, and get it to you: shipped NZ-wide or Auckland
                pickup/drop-off on a flat $170 — or fully installed by us for
                $200.
              </p>
              <Link
                to="/about"
                data-testid="home-about-link"
                className="mt-8 inline-flex items-center gap-2 rounded-[3px] border border-black/25 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-ink transition-colors duration-200 hover:border-volt hover:text-volt"
              >
                Read our story <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- PRICE / CTA BAND ---------- */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl border border-volt/25 bg-surface px-8 py-14 sm:px-14 volt-glow">
            <p className="eyebrow mb-4">Ready when you are</p>
            <h2 className="max-w-2xl font-display text-3xl font-extrabold tracking-tight text-ink sm:text-5xl">
              Combos from{" "}
              <span className="font-mono text-volt">
                {products[0] ? formatNZD(products[0].base_price) : "—"}
              </span>
              .
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-zinc-600">
              No card needed online. Place your order, we confirm stock, then
              invoice you directly. Simple.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/shop"
                data-testid="home-bottom-cta-shop"
                className="rounded-[3px] bg-volt px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-volt-dim"
              >
                Shop Kits
              </Link>
              <Link
                to="/accessories"
                data-testid="home-bottom-cta-accessories"
                className="rounded-[3px] border border-black/25 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-ink transition-colors duration-200 hover:border-volt hover:text-volt"
              >
                Browse Accessories
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
